import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  addDoc,
  getDocs,
  deleteDoc,
  updateDoc,
  doc,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, storage } from "../firebase/firebase";

import Sidebar from "../components/layout/Sidebar";
import Topbar from "../components/layout/Topbar";

import "./ProdutosBeleza.css";

const CATEGORY_OPTIONS = [
  "🔥 Emagrecimento & Metabolismo",
  "🌸 Beleza da Pele",
  "💁‍♀️ Cabelos & Crescimento Capilar",
  "⚡ Energia & Performance",
  "💓 Bem-Estar Sexual",
  "👩 Saúde da Mulher",
  "👨 Saúde do Homem",
  "💊 Vitaminas & Imunidade",
  "😴 Relaxamento & Sono",
];

const emptyForm = {
  title: "",
  category: CATEGORY_OPTIONS[0],
  description: "",
  affiliateLink: "",
  officialSite: "",
  image: null,
  bestSeller: false,
  popular: false,
  natural: false,
  payOnDelivery: false,
};

export default function ProdutosBeleza() {
  // FORM (Adicionar)
  const [formData, setFormData] = useState({ ...emptyForm });

  // LISTA
  const [products, setProducts] = useState([]);

  // LOADING
  const [loading, setLoading] = useState(false);

  // MODAL (Editar)
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [editForm, setEditForm] = useState({ ...emptyForm });
  const [editSaving, setEditSaving] = useState(false);

  useEffect(() => {
    loadProducts();
  }, []);

  const loadProducts = async () => {
    const snapshot = await getDocs(collection(db, "beauty_products"));
    setProducts(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
  };

  // Helpers
  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleEditChange = (field, value) => {
    setEditForm((prev) => ({ ...prev, [field]: value }));
  };

  const resetForm = () => {
    setFormData({ ...emptyForm });
  };

  const closeEditModal = () => {
    setEditModalOpen(false);
    setEditingProduct(null);
    setEditForm({ ...emptyForm });
  };

  const counts = useMemo(() => {
    const total = products.length;
    const withImage = products.filter((p) => !!p.imageUrl).length;
    const withLinks = products.filter((p) => !!p.affiliateLink || !!p.officialSite).length;
    return { total, withImage, withLinks };
  }, [products]);

  // Preview (Adicionar)
  const imagePreview = useMemo(() => {
    if (!formData.image) return null;
    try {
      return URL.createObjectURL(formData.image);
    } catch {
      return null;
    }
  }, [formData.image]);

  // Preview (Editar)
  const editImagePreview = useMemo(() => {
    if (!editForm.image) return null;
    try {
      return URL.createObjectURL(editForm.image);
    } catch {
      return null;
    }
  }, [editForm.image]);

  // ✅ Submit (Adicionar)
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim()) return;

    setLoading(true);
    try {
      let imageUrl = null;

      if (formData.image) {
        const imageRef = ref(storage, `beauty/${Date.now()}_${formData.image.name}`);
        await uploadBytes(imageRef, formData.image);
        imageUrl = await getDownloadURL(imageRef);
      }

      const productData = {
        title: formData.title,
        category: formData.category,
        description: formData.description,
        affiliateLink: formData.affiliateLink,
        officialSite: formData.officialSite,
        best_seller: formData.bestSeller,
        popular: formData.popular,
        natural: formData.natural,
        pay_on_delivery: formData.payOnDelivery,
        imageUrl,
        createdAt: new Date(),
      };

      await addDoc(collection(db, "beauty_products"), productData);

      resetForm();
      await loadProducts();
    } catch (err) {
      console.error(err);
      alert("❌ Erro ao salvar: " + (err?.message || "tente novamente."));
    } finally {
      setLoading(false);
    }
  };

  // ✅ Abrir modal de edição
  const handleEdit = (product) => {
    setEditingProduct(product);

    setEditForm({
      title: product.title || "",
      category: product.category || CATEGORY_OPTIONS[0],
      description: product.description || "",
      affiliateLink: product.affiliateLink || "",
      officialSite: product.officialSite || "",
      image: null,
      bestSeller: product.best_seller || false,
      popular: product.popular || false,
      natural: product.natural || false,
      payOnDelivery: product.pay_on_delivery || false,
    });

    setEditModalOpen(true);
  };

  // ✅ Salvar edição (modal)
  const handleSaveEdit = async (e) => {
    e.preventDefault();
    if (!editingProduct?.id) return;

    setEditSaving(true);
    try {
      // 🔥 Mantém imagem antiga se não escolher nova
      let imageUrl = editingProduct.imageUrl || null;

      if (editForm.image) {
        const imageRef = ref(storage, `beauty/${Date.now()}_${editForm.image.name}`);
        await uploadBytes(imageRef, editForm.image);
        imageUrl = await getDownloadURL(imageRef);
      }

      const productData = {
        title: editForm.title,
        category: editForm.category,
        description: editForm.description,
        affiliateLink: editForm.affiliateLink,
        officialSite: editForm.officialSite,
        best_seller: editForm.bestSeller,
        popular: editForm.popular,
        natural: editForm.natural,
        pay_on_delivery: editForm.payOnDelivery,
        imageUrl,
        updatedAt: new Date(),
      };

      await updateDoc(doc(db, "beauty_products", editingProduct.id), productData);

      await loadProducts();
      closeEditModal();
    } catch (err) {
      console.error(err);
      alert("❌ Erro ao atualizar: " + (err?.message || "tente novamente."));
    } finally {
      setEditSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Remover produto?")) {
      await deleteDoc(doc(db, "beauty_products", id));
      loadProducts();
    }
  };

  return (
    <div className="pb-page">
      <Sidebar />

      <div className="pb-main">
        <Topbar />

        <div className="pb-container">
          {/* HEADER */}
          <div className="pb-header">
            <div className="pb-title-wrap">
              <h1 className="pb-title">Catálogo — Beleza & Bem-Estar</h1>
              <p className="pb-subtitle">
                Cadastre produtos, links e tags para exibição no app/site.
              </p>
            </div>

            <div className="pb-metrics">
              <div className="pb-metric">
                <div className="pb-metric-num">{counts.total}</div>
                <div className="pb-metric-label">Produtos</div>
              </div>
              <div className="pb-metric">
                <div className="pb-metric-num">{counts.withImage}</div>
                <div className="pb-metric-label">Com imagem</div>
              </div>
              <div className="pb-metric">
                <div className="pb-metric-num">{counts.withLinks}</div>
                <div className="pb-metric-label">Com links</div>
              </div>
            </div>
          </div>

          {/* GRID */}
          <div className="pb-grid">
            {/* FORM (Adicionar) */}
            <section className="pb-card pb-card-form">
              <div className="pb-card-head">
                <div className="pb-card-head-left">
                  <h2 className="pb-card-title">➕ Adicionar Produto</h2>
                  <div className="pb-hint">
                    Dica: use descrição curta e objetiva (2–3 linhas).
                  </div>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="pb-form">
                <div className="pb-field">
                  <label className="pb-label">Nome do Produto</label>
                  <input
                    className="pb-input"
                    type="text"
                    value={formData.title}
                    onChange={(e) => handleInputChange("title", e.target.value)}
                    placeholder="Digite o nome do produto"
                    required
                  />
                </div>

                <div className="pb-field">
                  <label className="pb-label">Categoria</label>
                  <select
                    className="pb-select"
                    value={formData.category}
                    onChange={(e) => handleInputChange("category", e.target.value)}
                  >
                    {CATEGORY_OPTIONS.map((cat, idx) => (
                      <option key={idx} value={cat}>
                        {cat}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="pb-field">
                  <label className="pb-label">Descrição</label>
                  <textarea
                    className="pb-textarea"
                    value={formData.description}
                    onChange={(e) => handleInputChange("description", e.target.value)}
                    placeholder="Descreva o produto..."
                    rows={3}
                  />
                </div>

                <div className="pb-divider" />

                <div className="pb-tags">
                  <div className="pb-tags-title">Tags do Produto</div>

                  <label className={`pb-tag ${formData.bestSeller ? "is-on" : ""}`}>
                    <input
                      type="checkbox"
                      checked={formData.bestSeller}
                      onChange={(e) => handleInputChange("bestSeller", e.target.checked)}
                    />
                    <span className="pb-tag-icon">🔥</span>
                    <span className="pb-tag-text">Mais Vendido</span>
                  </label>

                  <label className={`pb-tag ${formData.popular ? "is-on" : ""}`}>
                    <input
                      type="checkbox"
                      checked={formData.popular}
                      onChange={(e) => handleInputChange("popular", e.target.checked)}
                    />
                    <span className="pb-tag-icon">💖</span>
                    <span className="pb-tag-text">Mais Procurado</span>
                  </label>

                  <label className={`pb-tag ${formData.natural ? "is-on" : ""}`}>
                    <input
                      type="checkbox"
                      checked={formData.natural}
                      onChange={(e) => handleInputChange("natural", e.target.checked)}
                    />
                    <span className="pb-tag-icon">🌿</span>
                    <span className="pb-tag-text">Produto Natural</span>
                  </label>

                  <label className={`pb-tag ${formData.payOnDelivery ? "is-on" : ""}`}>
                    <input
                      type="checkbox"
                      checked={formData.payOnDelivery}
                      onChange={(e) => handleInputChange("payOnDelivery", e.target.checked)}
                    />
                    <span className="pb-tag-icon">💵</span>
                    <span className="pb-tag-text">Pagar na Entrega</span>
                  </label>
                </div>

                <div className="pb-divider" />

                <div className="pb-two-cols">
                  <div className="pb-field">
                    <label className="pb-label">Link Comprar Agora</label>
                    <input
                      className="pb-input"
                      type="url"
                      value={formData.affiliateLink}
                      onChange={(e) => handleInputChange("affiliateLink", e.target.value)}
                      placeholder="https://exemplo.com/comprar"
                    />
                    <div className="pb-helper">
                      Usado para botão <strong>Comprar</strong>.
                    </div>
                  </div>

                  <div className="pb-field">
                    <label className="pb-label">Site Oficial</label>
                    <input
                      className="pb-input"
                      type="url"
                      value={formData.officialSite}
                      onChange={(e) => handleInputChange("officialSite", e.target.value)}
                      placeholder="https://exemplo.com"
                    />
                    <div className="pb-helper">
                      Usado para botão <strong>Site</strong>.
                    </div>
                  </div>
                </div>

                <div className="pb-divider" />

                <div className="pb-field">
                  <label className="pb-label">Imagem do Produto</label>

                  <div className="pb-upload">
                    <div className="pb-upload-preview">
                      {imagePreview ? (
                        <img src={imagePreview} alt="Prévia" />
                      ) : (
                        <div className="pb-upload-empty">
                          <span className="pb-upload-emoji">🖼️</span>
                          <span>Sem imagem</span>
                        </div>
                      )}
                    </div>

                    <div className="pb-upload-actions">
                      <input
                        className="pb-file"
                        type="file"
                        accept="image/*"
                        onChange={(e) =>
                          handleInputChange("image", e.target.files?.[0] || null)
                        }
                      />

                      <div className="pb-upload-tip">
                        Dica: imagens em 16:9 ficam mais bonitas no card.
                      </div>
                    </div>
                  </div>
                </div>

                <button
                  type="submit"
                  className="pb-btn pb-btn-primary"
                  disabled={loading}
                >
                  {loading ? "Salvando..." : "Adicionar Produto"}
                </button>
              </form>
            </section>

            {/* LISTA */}
            <section className="pb-card pb-card-list">
              <div className="pb-card-head">
                <h2 className="pb-card-title">📦 Produtos Cadastrados</h2>
                <button
                  type="button"
                  className="pb-btn pb-btn-ghost"
                  onClick={loadProducts}
                  title="Recarregar"
                >
                  Recarregar
                </button>
              </div>

              {products.length === 0 ? (
                <div className="pb-empty">
                  <div className="pb-empty-icon">🧾</div>
                  <div className="pb-empty-title">Nenhum produto cadastrado</div>
                  <div className="pb-empty-sub">
                    Cadastre o primeiro produto usando o formulário.
                  </div>
                </div>
              ) : (
                <div className="pb-products-grid">
                  {products.map((product) => (
                    <div className="pb-product" key={product.id}>
                      <div className="pb-product-media">
                        {product.imageUrl ? (
                          <img src={product.imageUrl} alt={product.title} />
                        ) : (
                          <div className="pb-product-noimg">
                            <span>Sem imagem</span>
                          </div>
                        )}

                        <div className="pb-product-badges">
                          {product.best_seller && <span className="pb-badge">🔥 Mais Vendido</span>}
                          {product.popular && <span className="pb-badge">💖 Mais Procurado</span>}
                          {product.natural && <span className="pb-badge">🌿 Natural</span>}
                          {product.pay_on_delivery && <span className="pb-badge">💵 Entrega</span>}
                        </div>
                      </div>

                      <div className="pb-product-body">
                        <div className="pb-product-title">{product.title}</div>

                        <div className="pb-product-category">{product.category}</div>

                        <div className="pb-product-desc">
                          {product.description || "Sem descrição."}
                        </div>

                        <div className="pb-product-links">
                          {product.affiliateLink ? (
                            <a
                              className="pb-link pb-link-buy"
                              href={product.affiliateLink}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              Comprar
                            </a>
                          ) : (
                            <span className="pb-link pb-link-disabled">Sem link</span>
                          )}

                          {product.officialSite ? (
                            <a
                              className="pb-link pb-link-site"
                              href={product.officialSite}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              Site
                            </a>
                          ) : (
                            <span className="pb-link pb-link-disabled">Sem site</span>
                          )}
                        </div>

                        <div className="pb-product-actions">
                          <button
                            className="pb-btn pb-btn-info"
                            onClick={() => handleEdit(product)}
                          >
                            Editar
                          </button>
                          <button
                            className="pb-btn pb-btn-danger"
                            onClick={() => handleDelete(product.id)}
                          >
                            Remover
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>
          </div>
        </div>
      </div>

      {/* =========================
          MODAL DE EDIÇÃO
      ========================= */}
      {editModalOpen && (
        <div className="pb-modal-overlay" onClick={closeEditModal}>
          <div className="pb-modal" onClick={(e) => e.stopPropagation()}>
            <div className="pb-modal-header">
              <div>
                <div className="pb-modal-title">✏️ Editar Produto</div>
                <div className="pb-modal-subtitle">
                  Atualize os dados e clique em <strong>Salvar</strong>.
                </div>
              </div>

              <button className="pb-modal-close" onClick={closeEditModal} aria-label="Fechar">
                ✕
              </button>
            </div>

            <form className="pb-modal-body" onSubmit={handleSaveEdit}>
              <div className="pb-modal-grid">
                <div className="pb-field">
                  <label className="pb-label">Nome do Produto</label>
                  <input
                    className="pb-input"
                    type="text"
                    value={editForm.title}
                    onChange={(e) => handleEditChange("title", e.target.value)}
                    required
                  />
                </div>

                <div className="pb-field">
                  <label className="pb-label">Categoria</label>
                  <select
                    className="pb-select"
                    value={editForm.category}
                    onChange={(e) => handleEditChange("category", e.target.value)}
                  >
                    {CATEGORY_OPTIONS.map((cat, idx) => (
                      <option key={idx} value={cat}>
                        {cat}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="pb-field pb-modal-span">
                  <label className="pb-label">Descrição</label>
                  <textarea
                    className="pb-textarea"
                    rows={3}
                    value={editForm.description}
                    onChange={(e) => handleEditChange("description", e.target.value)}
                  />
                </div>

                <div className="pb-divider pb-modal-span" />

                <div className="pb-tags pb-modal-span">
                  <div className="pb-tags-title">Tags do Produto</div>

                  <label className={`pb-tag ${editForm.bestSeller ? "is-on" : ""}`}>
                    <input
                      type="checkbox"
                      checked={editForm.bestSeller}
                      onChange={(e) => handleEditChange("bestSeller", e.target.checked)}
                    />
                    <span className="pb-tag-icon">🔥</span>
                    <span className="pb-tag-text">Mais Vendido</span>
                  </label>

                  <label className={`pb-tag ${editForm.popular ? "is-on" : ""}`}>
                    <input
                      type="checkbox"
                      checked={editForm.popular}
                      onChange={(e) => handleEditChange("popular", e.target.checked)}
                    />
                    <span className="pb-tag-icon">💖</span>
                    <span className="pb-tag-text">Mais Procurado</span>
                  </label>

                  <label className={`pb-tag ${editForm.natural ? "is-on" : ""}`}>
                    <input
                      type="checkbox"
                      checked={editForm.natural}
                      onChange={(e) => handleEditChange("natural", e.target.checked)}
                    />
                    <span className="pb-tag-icon">🌿</span>
                    <span className="pb-tag-text">Produto Natural</span>
                  </label>

                  <label className={`pb-tag ${editForm.payOnDelivery ? "is-on" : ""}`}>
                    <input
                      type="checkbox"
                      checked={editForm.payOnDelivery}
                      onChange={(e) => handleEditChange("payOnDelivery", e.target.checked)}
                    />
                    <span className="pb-tag-icon">💵</span>
                    <span className="pb-tag-text">Pagar na Entrega</span>
                  </label>
                </div>

                <div className="pb-divider pb-modal-span" />

                <div className="pb-field">
                  <label className="pb-label">Link Comprar Agora</label>
                  <input
                    className="pb-input"
                    type="url"
                    value={editForm.affiliateLink}
                    onChange={(e) => handleEditChange("affiliateLink", e.target.value)}
                    placeholder="https://exemplo.com/comprar"
                  />
                </div>

                <div className="pb-field">
                  <label className="pb-label">Site Oficial</label>
                  <input
                    className="pb-input"
                    type="url"
                    value={editForm.officialSite}
                    onChange={(e) => handleEditChange("officialSite", e.target.value)}
                    placeholder="https://exemplo.com"
                  />
                </div>

                <div className="pb-field pb-modal-span">
                  <label className="pb-label">Imagem do Produto</label>

                  <div className="pb-upload">
                    <div className="pb-upload-preview">
                      {editImagePreview ? (
                        <img src={editImagePreview} alt="Prévia nova" />
                      ) : editingProduct?.imageUrl ? (
                        <img src={editingProduct.imageUrl} alt="Imagem atual" />
                      ) : (
                        <div className="pb-upload-empty">
                          <span className="pb-upload-emoji">🖼️</span>
                          <span>Sem imagem</span>
                        </div>
                      )}
                    </div>

                    <div className="pb-upload-actions">
                      <input
                        className="pb-file"
                        type="file"
                        accept="image/*"
                        onChange={(e) =>
                          handleEditChange("image", e.target.files?.[0] || null)
                        }
                      />
                      <div className="pb-upload-tip">
                        Se não escolher nova imagem, a atual será mantida.
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="pb-modal-footer">
                <button
                  type="button"
                  className="pb-btn pb-btn-ghost"
                  onClick={closeEditModal}
                >
                  Cancelar
                </button>

                <button
                  type="submit"
                  className="pb-btn pb-btn-primary pb-btn-primary-inline"
                  disabled={editSaving}
                >
                  {editSaving ? "Salvando..." : "Salvar Alterações"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
